// src/components/trades/mockups/TradeProposalMockupA.js
// OPTION A: Split-Screen Side-by-Side Layout (Traditional Desktop)

import React, { useState } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { ArrowRightLeft, ChevronDown, DollarSign } from 'lucide-react';

/**
 * MOCKUP A: Split-Screen Side-by-Side Layout
 * - Two equal columns showing each team's roster
 * - Central divider with trade arrow
 * - Selected assets highlighted
 * - Best for desktop/wide screens
 */
const TradeProposalMockupA = () => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [mySelectedPlayers, setMySelectedPlayers] = useState([]);
  const [theirSelectedPlayers, setTheirSelectedPlayers] = useState([]);
  const [mySelectedPicks, setMySelectedPicks] = useState([]);
  const [theirSelectedPicks, setTheirSelectedPicks] = useState([]);

  // Mock data
  const mockTeams = [
    { team_id: '1', team_name: 'Thunder Bats' },
    { team_id: '2', team_name: 'Dynasty Kings' },
    { team_id: '3', team_name: 'Home Run Heroes' }
  ];

  const mockMyRoster = [
    {
      id: 1,
      name: 'Juan Soto',
      info: { mlb_team: 'NYY', position: 'OF' },
      games: 145,
      ab: 550,
      hits: 165,
      avg: '.300',
      hr: 41,
      rbi: 109,
      sb: 7,
      price: 65,
      salary: 62,
      contract: 3,
      rolling_avg: '.312',
      rolling_hr: 8,
      rolling_rbi: 21
    },
    {
      id: 2,
      name: 'Ronald Acuña Jr.',
      info: { mlb_team: 'ATL', position: 'OF' },
      games: 119,
      ab: 455,
      hits: 149,
      avg: '.327',
      hr: 29,
      rbi: 85,
      sb: 43,
      price: 72,
      salary: 68,
      contract: 4,
      rolling_avg: '.298',
      rolling_hr: 4,
      rolling_rbi: 12
    },
    {
      id: 3,
      name: 'Gerrit Cole',
      info: { mlb_team: 'NYY', position: 'SP' },
      games: 30,
      starts: 30,
      wins: 14,
      qs: 19,
      k: 209,
      saves: 0,
      era: '2.87',
      whip: '1.02',
      price: 58,
      salary: 55,
      contract: 2,
      rolling_era: '2.45',
      rolling_k: 42,
      rolling_qs: 4
    }
  ];

  const mockTheirRoster = [
    {
      id: 4,
      name: 'Mookie Betts',
      info: { mlb_team: 'LAD', position: 'OF' },
      games: 152,
      ab: 575,
      hits: 179,
      avg: '.311',
      hr: 35,
      rbi: 97,
      sb: 12,
      price: 68,
      salary: 65,
      contract: 3,
      rolling_avg: '.329',
      rolling_hr: 7,
      rolling_rbi: 18
    },
    {
      id: 5,
      name: 'Corbin Burnes',
      info: { mlb_team: 'BAL', position: 'SP' },
      games: 32,
      starts: 32,
      wins: 15,
      qs: 22,
      k: 181,
      saves: 0,
      era: '3.19',
      whip: '1.09',
      price: 52,
      salary: 48,
      contract: 2,
      rolling_era: '2.98',
      rolling_k: 38,
      rolling_qs: 5
    }
  ];

  const mockMyPicks = [
    { pick_id: '1', year: 2026, round: 1, overall: 3 },
    { pick_id: '2', year: 2026, round: 3, overall: 27 },
    { pick_id: '3', year: 2027, round: 1, overall: 5 }
  ];

  const mockTheirPicks = [
    { pick_id: '4', year: 2026, round: 2, overall: 14 },
    { pick_id: '5', year: 2027, round: 1, overall: 2 },
    { pick_id: '6', year: 2027, round: 2, overall: 18 }
  ];

  const togglePlayerSelection = (playerId, isMine) => {
    if (isMine) {
      setMySelectedPlayers(prev => 
        prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
      );
    } else {
      setTheirSelectedPlayers(prev => 
        prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
      );
    }
  };

  const togglePickSelection = (pickId, isMine) => {
    if (isMine) {
      setMySelectedPicks(prev => 
        prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
      );
    } else {
      setTheirSelectedPicks(prev => 
        prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
          OPTION A: Split-Screen Layout
        </h4>
        <div className={`px-3 py-1 ${dynastyTheme.components.badge.info} text-xs`}>
          Desktop-Optimized
        </div>
      </div>

      {/* Team Selector */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
          Select Trading Partner
        </label>
        <select
          value={selectedTeam || ''}
          onChange={(e) => setSelectedTeam(e.target.value)}
          className={`w-full md:w-64 px-4 py-2 ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} rounded-lg`}
        >
          <option value="">Choose a team...</option>
          {mockTeams.map(team => (
            <option key={team.team_id} value={team.team_id}>
              {team.team_name}
            </option>
          ))}
        </select>
      </div>

      {/* Split-Screen Layout */}
      {selectedTeam && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT SIDE - Your Team */}
          <div className={`${dynastyTheme.components.card.base} p-4 space-y-4`}>
            <div className="border-b border-neutral-700 pb-3">
              <h5 className={`font-bold ${dynastyTheme.classes.text.primary}`}>
                YOUR TEAM - Thunder Bats
              </h5>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                Select players and picks to trade away
              </p>
            </div>

            {/* Players Section */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                PLAYERS ({mySelectedPlayers.length} selected)
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {mockMyRoster.map(player => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    selected={mySelectedPlayers.includes(player.id)}
                    onToggle={() => togglePlayerSelection(player.id, true)}
                  />
                ))}
              </div>
            </div>

            {/* Draft Picks Section */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                DRAFT PICKS ({mySelectedPicks.length} selected)
              </div>
              <div className="space-y-2">
                {mockMyPicks.map(pick => (
                  <PickCard
                    key={pick.pick_id}
                    pick={pick}
                    selected={mySelectedPicks.includes(pick.pick_id)}
                    onToggle={() => togglePickSelection(pick.pick_id, true)}
                  />
                ))}
              </div>
            </div>

            {/* Cash Section - Placeholder */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                CASH (FAAB)
              </div>
              <div className={`p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg border border-dashed border-neutral-600`}>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-neutral-500" />
                  <input
                    type="number"
                    placeholder="$0"
                    disabled
                    className={`flex-1 bg-transparent ${dynastyTheme.classes.text.neutralLight} text-sm`}
                  />
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLighter}`}>
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Their Team */}
          <div className={`${dynastyTheme.components.card.base} p-4 space-y-4`}>
            <div className="border-b border-neutral-700 pb-3">
              <h5 className={`font-bold ${dynastyTheme.classes.text.warning}`}>
                THEIR TEAM - {mockTeams.find(t => t.team_id === selectedTeam)?.team_name}
              </h5>
              <p className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                Select players and picks to receive
              </p>
            </div>

            {/* Players Section */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                PLAYERS ({theirSelectedPlayers.length} selected)
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {mockTheirRoster.map(player => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    selected={theirSelectedPlayers.includes(player.id)}
                    onToggle={() => togglePlayerSelection(player.id, false)}
                  />
                ))}
              </div>
            </div>

            {/* Draft Picks Section */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                DRAFT PICKS ({theirSelectedPicks.length} selected)
              </div>
              <div className="space-y-2">
                {mockTheirPicks.map(pick => (
                  <PickCard
                    key={pick.pick_id}
                    pick={pick}
                    selected={theirSelectedPicks.includes(pick.pick_id)}
                    onToggle={() => togglePickSelection(pick.pick_id, false)}
                  />
                ))}
              </div>
            </div>

            {/* Cash Section - Placeholder */}
            <div>
              <div className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                CASH (FAAB)
              </div>
              <div className={`p-3 ${dynastyTheme.classes.bg.darkLighter} rounded-lg border border-dashed border-neutral-600`}>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-neutral-500" />
                  <input
                    type="number"
                    placeholder="$0"
                    disabled
                    className={`flex-1 bg-transparent ${dynastyTheme.classes.text.neutralLight} text-sm`}
                  />
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLighter}`}>
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trade Summary & Submit */}
      {selectedTeam && (
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h5 className={`font-bold ${dynastyTheme.classes.text.white} mb-1`}>
                Trade Summary
              </h5>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                You give {mySelectedPlayers.length + mySelectedPicks.length} assets • 
                You receive {theirSelectedPlayers.length + theirSelectedPicks.length} assets
              </p>
            </div>
            <button
              disabled={!mySelectedPlayers.length && !mySelectedPicks.length && !theirSelectedPlayers.length && !theirSelectedPicks.length}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Propose Trade
            </button>
          </div>

          {/* Notes */}
          <textarea
            placeholder="Add a message to your trade proposal (optional)"
            rows={2}
            className={`w-full px-3 py-2 ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} rounded-lg text-sm`}
          />
        </div>
      )}
    </div>
  );
};

// Player Card Component
const PlayerCard = ({ player, selected, onToggle }) => {
  const isHitter = ['OF', 'SS', 'C', '1B', '2B', '3B', 'DH'].includes(player.info?.position);

  return (
    <div
      onClick={onToggle}
      className={`
        p-3 rounded-lg border cursor-pointer transition-all
        ${selected 
          ? `${dynastyTheme.classes.border.primary} bg-yellow-400/10`
          : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
        }
        hover:border-yellow-400/50
      `}
    >
      {/* Player Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className={`font-medium ${dynastyTheme.classes.text.white}`}>
            {player.name}
          </div>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {player.info?.mlb_team} • {player.info?.position}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xs ${dynastyTheme.classes.text.primary} font-bold`}>
            ${player.salary}
          </div>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {player.contractType?.startsWith('OP') ? player.contractType : `${player.contract} yr`}
          </div>
        </div>
      </div>

      {/* Season Stats */}
      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-1`}>
        {isHitter ? (
          <div className="flex gap-3">
            <span>G:{player.games}</span>
            <span>AB:{player.ab}</span>
            <span>H:{player.hits}</span>
            <span>AVG:{player.avg}</span>
            <span>HR:{player.hr}</span>
            <span>RBI:{player.rbi}</span>
            <span>SB:{player.sb}</span>
          </div>
        ) : (
          <div className="flex gap-3">
            <span>G:{player.games}</span>
            <span>GS:{player.starts}</span>
            <span>W:{player.wins}</span>
            <span>QS:{player.qs}</span>
            <span>K:{player.k}</span>
            <span>ERA:{player.era}</span>
            <span>WHIP:{player.whip}</span>
          </div>
        )}
      </div>

      {/* 14-Day Rolling Stats */}
      <div className={`text-xs ${dynastyTheme.classes.text.neutralLighter} italic`}>
        14d: {isHitter ? (
          <span>AVG:{player.rolling_avg} HR:{player.rolling_hr} RBI:{player.rolling_rbi}</span>
        ) : (
          <span>ERA:{player.rolling_era} K:{player.rolling_k} QS:{player.rolling_qs}</span>
        )}
      </div>
    </div>
  );
};

// Pick Card Component
const PickCard = ({ pick, selected, onToggle }) => {
  return (
    <div
      onClick={onToggle}
      className={`
        p-2 rounded-lg border cursor-pointer transition-all
        ${selected 
          ? `${dynastyTheme.classes.border.primary} bg-yellow-400/10`
          : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
        }
        hover:border-yellow-400/50
      `}
    >
      <div className="flex items-center justify-between">
        <div className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
          {pick.year} Round {pick.round}
        </div>
        <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
          Pick #{pick.overall}
        </div>
      </div>
    </div>
  );
};

export default TradeProposalMockupA;
